/*
 * Bundle Builder — AU compression socks only
 * Loaded conditionally from sections/main-product.liquid when:
 *   localization.market.handle == 'au'
 *   AND product is in the `compression-socks` collection
 *
 * Pairs with: snippets/bundle-builder-au.liquid + assets/bundle-builder-au.css
 *
 * Mirrors the custom-element + /cart/add.js + PUB_SUB_EVENTS.cartUpdate
 * pattern used in assets/product-form.js.
 */
if (!customElements.get('bundle-builder-au')) {
  customElements.define(
    'bundle-builder-au',
    class BundleBuilderAU extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
      }

      connectedCallback() {
        if (this.initialized) return;
        try {
          this.init();
          this.initialized = true;
        } catch (err) {
          console.error('[bundle-builder-au] init failed', err);
        }
      }

      /* ---------- Init ---------- */

      init() {
        const dataScript = this.querySelector('script[data-bb-data]');
        if (!dataScript) throw new Error('missing data script');
        this.data = JSON.parse(dataScript.textContent);

        // Elements
        this.packButtons = Array.from(this.querySelectorAll('[data-bb-pack]'));
        this.sizeButtons = Array.from(this.querySelectorAll('[data-bb-size]'));
        this.styleButtons = Array.from(this.querySelectorAll('[data-bb-style]'));
        this.slotsEl = this.querySelector('[data-bb-slots]');
        this.atcBtn = this.querySelector('[data-bb-atc]');
        this.atcLabel = this.querySelector('[data-bb-atc-label]');
        this.errorEl = this.querySelector('[data-bb-error]');

        // State
        this.packTier = 1;

        // Default size: first button in the fixed list that the *current*
        // product has as an available variant. Falls back to the first
        // button if the product has no matching size (the seed slot will
        // then render as unavailable and prompt the shopper to change size).
        const currentVariants = (this.data.currentProduct && this.data.currentProduct.variants) || {};
        let defaultSize = '';
        for (const btn of this.sizeButtons) {
          const code = btn.dataset.bbSize;
          const entry = currentVariants[code];
          if (entry && entry.available !== false) {
            defaultSize = code;
            break;
          }
        }
        if (!defaultSize && this.sizeButtons[0]) {
          defaultSize = this.sizeButtons[0].dataset.bbSize || '';
        }
        this.sizeCode = defaultSize;

        // Sync button selected state to the resolved default.
        this.sizeButtons.forEach((btn) => {
          const isSelected = btn.dataset.bbSize === this.sizeCode;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });

        this.slots = []; // Array<Slot>

        // Seed slot 1 with the current product at the default size (req #5)
        const seed = this.buildSlotForProduct(this.data.currentProduct, this.sizeCode);
        if (seed) this.slots.push(seed);

        // Event wiring
        this.packButtons.forEach((btn) =>
          btn.addEventListener('click', () => this.onPackClick(btn))
        );
        this.sizeButtons.forEach((btn) =>
          btn.addEventListener('click', () => this.onSizeClick(btn))
        );
        this.styleButtons.forEach((btn) => {
          btn.addEventListener('click', (e) => this.onStyleClick(btn, e));

          // Wire the "remove all of this style" X overlay directly so it
          // runs before the button's click handler and cannot be swallowed
          // by any DOM stacking / hit-testing edge cases.
          const xBtn = btn.querySelector('[data-bb-style-remove]');
          if (xBtn) {
            const removeHandler = (e) => {
              e.stopPropagation();
              e.preventDefault();
              const pid = parseInt(btn.dataset.productId, 10);
              this.removeAllOfStyle(pid);
            };
            xBtn.addEventListener('click', removeHandler);
            // Touch devices fire pointerdown before click — prevent the
            // outer button from starting its own selection gesture.
            xBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
          }
        });
        this.slotsEl.addEventListener('click', (e) => {
          const rm = e.target.closest('[data-bb-remove]');
          if (rm) this.onRemoveSlot(parseInt(rm.dataset.bbRemove, 10));
        });
        this.atcBtn.addEventListener('click', () => this.onAddToCart());

        // Size guide popup — intercept the link click and show an in-page modal
        const sizeGuideLink = this.querySelector('.bb-size-guide');
        if (sizeGuideLink) {
          sizeGuideLink.addEventListener('click', (e) => {
            e.preventDefault();
            this.openSizeGuideModal(sizeGuideLink.href);
          });
        }

        // Cart drawer reference (for sections-render optimisation)
        this.cart =
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-notification');
        if (this.cart) this.atcBtn.setAttribute('aria-haspopup', 'dialog');

        // Hide legacy picker / size / style-swatch / buy-button blocks that
        // live next to the bundle builder on compression sock PDPs. The
        // bundle builder replaces them wholesale.
        this.hideLegacyBlocks();

        // Intercept Shop Pay / Afterpay / PayPal / Google Pay dynamic
        // checkout clicks so the full bundle gets added to cart first.
        this.setupDynamicCheckoutIntercept();

        // Initial render
        this.renderPackCards();
        this.renderSlots();
        this.renderStyles();
        this.renderAtc();
      }

      /* ---------- Legacy block hiding ---------- */

      hideLegacyBlocks() {
        const parent = this.parentElement;
        if (!parent) return;

        // Wholesale-hide these legacy blocks (none contain the payment button)
        const legacySelectors = [
          '.sock-style-selector',
          '.sock-style-selected-name',
          '.variants-size-box',
          'variant-radios',
          'variant-selects',
          'quantity-input',
        ];
        legacySelectors.forEach((sel) => {
          parent.querySelectorAll(sel).forEach((el) => {
            el.style.setProperty('display', 'none', 'important');
          });
        });

        // The theme renders the buy buttons inside:
        //   .buy--buttons > product-form.product-form > form > .product-form__buttons
        //     └─ button[name="add"]  (hide — bundle builder has its own ATC)
        //     └─ .shopify-payment-button  (KEEP — these are the dynamic
        //        checkout buttons: Shop Pay, Afterpay, PayPal, etc.)
        //
        // We cannot detach the payment button from its form without breaking
        // it (Shopify's script owns the form reference). So: keep the form
        // intact, hide only the bits we don't want, and relocate the whole
        // <product-form> (or buy--buttons wrapper) to sit directly below our
        // bundle-builder ATC.
        try {
          const productForm =
            parent.querySelector('product-form.product-form') ||
            parent.querySelector('.product-form') ||
            document.querySelector('.product__info-container product-form.product-form');
          if (productForm) {
            // Hide the inline ATC button and the error wrapper inside the form
            productForm
              .querySelectorAll('button[name="add"], .product-form__error-message-wrapper')
              .forEach((el) => el.style.setProperty('display', 'none', 'important'));

            // The theme renders the hidden `name="id"` input with `disabled`;
            // the theme's variant picker normally flips that off when a
            // variant is chosen. We've hidden the variant picker, so the
            // input stays disabled and the Shopify dynamic checkout script
            // renders its buttons with zero height. Enable the input and
            // point it at the current product's first available variant so
            // Shop Pay / Afterpay / PayPal / Google Pay render and work.
            const variantIdInput = productForm.querySelector('input[name="id"]');
            if (variantIdInput) {
              variantIdInput.removeAttribute('disabled');
              const currentVariants = (this.data.currentProduct && this.data.currentProduct.variants) || {};
              let seedVariantId = '';
              for (const code in currentVariants) {
                const entry = currentVariants[code];
                if (entry && entry.available !== false && entry.id) {
                  seedVariantId = String(entry.id);
                  break;
                }
              }
              if (seedVariantId) {
                variantIdInput.value = seedVariantId;
                variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }

            // Move the form (with its payment button still inside) to appear
            // directly under our ATC, wrapped in a container we can style.
            const wrap =
              this.parentElement.querySelector('.bb-dynamic-checkout') ||
              document.createElement('div');
            wrap.className = 'bb-dynamic-checkout';
            if (!wrap.isConnected) {
              this.atcBtn.insertAdjacentElement('afterend', wrap);
            }
            wrap.appendChild(productForm);
          }
        } catch (err) {
          console.warn('[bundle-builder-au] could not relocate product form', err);
        }

        // Hide the buy--buttons wrapper IF it's now empty (the product form
        // we cared about was moved out). Leave it alone if it still has
        // content we didn't touch.
        parent.querySelectorAll('.buy--buttons').forEach((el) => {
          if (el.querySelector('product-form, .shopify-payment-button')) return;
          el.style.setProperty('display', 'none', 'important');
        });

        // Hide the bare "<b>Select Style:</b>" label that sits directly
        // before the sock-style-selected-name span. It has no class so we
        // locate it via the adjacent sibling.
        Array.from(parent.children).forEach((child) => {
          if (
            child.tagName === 'B' &&
            child.nextElementSibling &&
            child.nextElementSibling.classList &&
            child.nextElementSibling.classList.contains('sock-style-selected-name')
          ) {
            child.style.setProperty('display', 'none', 'important');
          }
        });

        // Also hide any HR immediately preceding the bundle builder that
        // was acting as a divider before the legacy picker, so we don't
        // leave a stray line above our UI.
        const prev = this.previousElementSibling;
        if (prev && prev.tagName === 'HR') {
          prev.style.setProperty('display', 'none', 'important');
        }
      }

      /* ---------- Slot helpers ---------- */

      buildSlotForProduct(prod, sizeCode) {
        if (!prod) return null;
        const variantEntry = prod.variants && prod.variants[sizeCode];
        if (!variantEntry) return null;
        return {
          productId: prod.id,
          productHandle: prod.handle,
          variantId: variantEntry.id,
          available: variantEntry.available !== false,
          title: prod.title,
          heroUrl: prod.hero,
          thumbUrl: prod.thumb,
          sizeCode: sizeCode,
        };
      }

      getStyleDataFromButton(btn) {
        let variants = {};
        try {
          variants = JSON.parse(btn.dataset.variants || '{}');
        } catch (_) {
          variants = {};
        }
        let images = [];
        try {
          images = JSON.parse(btn.dataset.images || '[]');
        } catch (_) {
          images = [];
        }
        // Normalise to the same shape as currentProduct.variants
        const prod = {
          id: parseInt(btn.dataset.productId, 10),
          handle: btn.dataset.productHandle,
          title: btn.dataset.productTitle,
          url: btn.dataset.productUrl,
          hero: btn.dataset.hero,
          thumb: btn.dataset.thumb,
          images, // [{ l: "<1500w url>", s: "<200w url>", a: "<alt>" }, …]
          variants,
        };
        return prod;
      }

      updateBasePriceForCurrentContext() {
        // Look up the price for the current size from the currently-displayed
        // product. Check style buttons for the last-selected style first,
        // then fall back to the initial currentProduct data.
        const lastSlot = this.slots.length ? this.slots[this.slots.length - 1] : null;
        let variants = null;
        if (lastSlot) {
          const btn = this.styleButtons.find(
            (b) => parseInt(b.dataset.productId, 10) === lastSlot.productId
          );
          if (btn) {
            try { variants = JSON.parse(btn.dataset.variants || '{}'); } catch (_) {}
          }
        }
        if (!variants && this.data.currentProduct) {
          variants = this.data.currentProduct.variants;
        }
        if (variants) {
          const entry = variants[this.sizeCode];
          if (entry && entry.price) {
            this.data.basePriceCents = entry.price;
          }
        }
      }

      /* ---------- Event handlers ---------- */

      onPackClick(btn) {
        const qty = parseInt(btn.dataset.bbPack, 10);
        if (!qty || qty === this.packTier) return;
        this.packTier = qty;

        // Shrink: keep first N. Grow: no immediate change, just allow more picks.
        if (this.slots.length > qty && qty < 4) {
          this.slots = this.slots.slice(0, qty);
        }

        this.renderPackCards();
        this.renderSlots();
        this.renderStyles();
        this.renderAtc();
      }

      onSizeClick(btn) {
        const next = btn.dataset.bbSize;
        if (!next || next === this.sizeCode) return;

        // Size selector is the "size for the next pick" — it does NOT
        // retroactively change the size of items already in the bundle.
        // Each slot carries its own sizeCode (set at the time of adding).
        this.sizeCode = next;

        // Update basePriceCents for the new size — check the last-selected
        // style (or current product) for a price at this size.
        this.updateBasePriceForCurrentContext();

        this.renderSizeButtons();
        this.renderPackCards();
        this.renderStyles(); // re-filter the style grid for the new next-pick size
        this.renderAtc();
      }

      onStyleClick(btn, e) {
        // If the click landed on the "remove all" X overlay, remove every
        // slot belonging to this style and bail out (do NOT add another).
        if (e && e.target && e.target.closest('[data-bb-style-remove]')) {
          e.stopPropagation();
          e.preventDefault();
          const pid = parseInt(btn.dataset.productId, 10);
          this.removeAllOfStyle(pid);
          return;
        }

        if (
          btn.classList.contains('is-oos') ||
          btn.classList.contains('is-oos-size') ||
          btn.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }
        const prod = this.getStyleDataFromButton(btn);
        const entry = prod.variants[this.sizeCode];
        if (!entry || entry.available === false) {
          this.showError('This style is sold out at the selected size.');
          return;
        }
        this.clearError();

        const nextSlot = {
          productId: prod.id,
          productHandle: prod.handle,
          variantId: entry.id,
          available: true,
          title: prod.title,
          heroUrl: prod.hero,
          thumbUrl: prod.thumb,
          sizeCode: this.sizeCode,
        };

        // Update basePriceCents to reflect the newly selected style's price
        // so the pack cards show accurate pricing for this product.
        if (entry.price) {
          this.data.basePriceCents = entry.price;
        }

        // Capacity logic:
        // - At tiers 1/2/3, if the bundle is already full, auto-upgrade the
        //   pack tier to accommodate the new pick (e.g. 2-pack with 2 items
        //   + new click → bump to 3-pack). Cap at 4; past 4 we're in
        //   uncapped "4+" mode and just append.
        // - At tier 4 the bundle is uncapped.
        const filled = this.slots.length;
        if (this.packTier < 4 && filled >= this.packTier) {
          this.packTier = Math.min(4, filled + 1);
        }

        this.slots.push(nextSlot);

        // Dynamic PDP title + hero update (req #2)
        this.updatePdpVisual(prod);

        this.renderPackCards();
        this.renderSlots();
        this.renderStyles();
        this.renderAtc();
      }

      onRemoveSlot(index) {
        if (Number.isNaN(index)) return;
        this.slots.splice(index, 1);
        this.renderPackCards();
        this.renderSlots();
        this.renderStyles();
        this.renderAtc();
      }

      removeAllOfStyle(productId) {
        if (!productId) return;
        const before = this.slots.length;
        this.slots = this.slots.filter((s) => s.productId !== productId);
        if (this.slots.length === before) return;
        this.renderPackCards();
        this.renderSlots();
        this.renderStyles();
        this.renderAtc();
      }

      /* ---------- PDP cosmetic update ---------- */

      updatePdpVisual(prod) {
        // Title — the main-product section's `title` block renders an h1 inside .product__title
        const titleEl =
          document.querySelector('.product__info-wrapper .product__title h1') ||
          document.querySelector('.product__title h1') ||
          document.querySelector('h1.product__title') ||
          document.querySelector('h1');
        if (titleEl && prod.title) titleEl.textContent = prod.title;

        // Resolve image list for this style. Fall back to [{l,s,a}] built
        // from the single hero/thumb URLs if the detailed images array is
        // missing (e.g. the current product which only has hero/thumb).
        let images = Array.isArray(prod.images) ? prod.images.slice() : [];
        if (!images.length && prod.hero) {
          images = [{ l: prod.hero, s: prod.thumb || prod.hero, a: prod.title || '' }];
        }

        // --- Main slides ---
        const slides = Array.from(document.querySelectorAll('.product__media-list .product__media-item'));
        slides.forEach((slide, i) => {
          const data = images[i];
          if (data) {
            // Update every <img> inside this slide (there can be several —
            // the main `img`, the `image-magnify-lightbox` img used by the
            // hover-zoom component, etc.). Swapping them all ensures the
            // expanded/zoomed view shows the new style, not the old.
            slide.querySelectorAll('img').forEach((img) => {
              img.setAttribute('src', data.l);
              img.removeAttribute('srcset');
              img.removeAttribute('data-srcset');
              if (data.a) img.setAttribute('alt', data.a);
            });
            slide.style.removeProperty('display');
          } else {
            // No corresponding image for this slot — hide the slide.
            slide.style.setProperty('display', 'none', 'important');
          }
        });

        // --- Expanded / lightbox view (product-modal) ---
        // The theme renders a <product-modal> with one slide per media-id.
        // Shoppers click the hero to open it. Swap the modal images too so
        // the lightbox reflects the newly-selected style.
        const modalSlides = Array.from(document.querySelectorAll('product-modal .product-media-modal__content, .product-media-modal__content'));
        modalSlides.forEach((node, i) => {
          const data = images[i];
          if (data) {
            node.querySelectorAll('img').forEach((img) => {
              img.setAttribute('src', data.l);
              img.removeAttribute('srcset');
              img.removeAttribute('data-srcset');
              if (data.a) img.setAttribute('alt', data.a);
            });
            node.style.removeProperty('display');
          } else {
            node.style.setProperty('display', 'none', 'important');
          }
        });

        // --- Thumbnail slider ---
        const thumbs = Array.from(document.querySelectorAll('.thumbnail-slider .thumbnail-list__item, .thumbnail-list .thumbnail-list__item'));
        thumbs.forEach((thumb, i) => {
          const img = thumb.querySelector('img');
          const data = images[i];
          if (data && img) {
            img.setAttribute('src', data.s);
            img.removeAttribute('srcset');
            img.removeAttribute('data-srcset');
            if (data.a) img.setAttribute('alt', data.a);
            thumb.style.removeProperty('display');
          } else {
            thumb.style.setProperty('display', 'none', 'important');
          }
        });

        // Snap the main slider back to the first slide (active state) so
        // shoppers see the new hero without needing to scroll the gallery.
        const firstSlide = slides[0];
        if (firstSlide) {
          slides.forEach((s) => s.classList.remove('is-active'));
          firstSlide.classList.add('is-active');
          const slider =
            firstSlide.closest('slider-component') ||
            firstSlide.closest('.product__media-list');
          if (slider && typeof slider.setActivePage === 'function') {
            try { slider.setActivePage(0); } catch (_) {}
          } else if (slider && slider.scrollTo) {
            try { slider.scrollTo({ left: 0, behavior: 'instant' }); } catch (_) {}
          }
        }
      }

      /* ---------- Rendering ---------- */

      renderPackCards() {
        const basePriceCents = Number(this.data.basePriceCents) || 0;
        const format = (cents) => this.formatMoney(cents);

        this.packButtons.forEach((btn) => {
          const qty = parseInt(btn.dataset.bbPack, 10);
          const discountPct = parseFloat(btn.dataset.bbDiscount) || 0;
          const perPair = Math.round(basePriceCents * (1 - discountPct / 100));
          const total = perPair * qty;

          const totalEl = btn.querySelector('[data-bb-pack-total]');
          const unitEl = btn.querySelector('[data-bb-pack-unit]');
          if (totalEl) totalEl.textContent = format(total);
          if (unitEl) unitEl.textContent = format(perPair) + '/pair';

          const isSelected = qty === this.packTier;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });
      }

      renderSizeButtons() {
        this.sizeButtons.forEach((btn) => {
          const isSelected = btn.dataset.bbSize === this.sizeCode;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });
      }

      renderStyles() {
        const selectedIds = new Set(this.slots.map((s) => s.productId));
        this.styleButtons.forEach((btn) => {
          const pid = parseInt(btn.dataset.productId, 10);
          const isInBundle = selectedIds.has(pid);
          btn.classList.toggle('is-selected', isInBundle);
          // `is-current` is baked in by Liquid for the product whose page
          // we're on. Once the shopper removes it from the bundle it must
          // no longer show the "in bundle" tick — so treat is-current as
          // a mirror of is-selected from here on.
          btn.classList.toggle('is-current', isInBundle && pid === this.data.currentProduct.id);

          // Mark as "sold out at current size" if the style has no variant
          // for this size, or the variant is unavailable. Keep the original
          // `.is-oos` class (fully OOS across all sizes) untouched — it's
          // applied server-side in Liquid.
          let oosForSize = true;
          try {
            const variants = JSON.parse(btn.dataset.variants || '{}');
            const entry = variants[this.sizeCode];
            oosForSize = !entry || entry.available === false;
          } catch (_) {
            oosForSize = true;
          }
          btn.classList.toggle('is-oos-size', oosForSize);
          if (oosForSize || btn.classList.contains('is-oos')) {
            btn.setAttribute('aria-disabled', 'true');
          } else {
            btn.removeAttribute('aria-disabled');
          }
        });
      }

      renderSlots() {
        // Capacity: at tiers 1-3 = tier; at tier 4+ = max(4, slots.length)
        const capacity =
          this.packTier < 4 ? this.packTier : Math.max(4, this.slots.length);

        const frag = document.createDocumentFragment();
        for (let i = 0; i < capacity; i++) {
          const slot = this.slots[i];
          const li = document.createElement('li');
          li.className = 'bb-slot ' + (slot ? 'is-filled' : 'is-empty');
          if (slot && slot.available === false) li.classList.add('is-warning');

          if (slot) {
            const img = document.createElement('img');
            img.src = slot.thumbUrl;
            img.alt = slot.title || '';
            img.loading = 'lazy';
            li.appendChild(img);

            const rm = document.createElement('button');
            rm.type = 'button';
            rm.className = 'bb-slot__remove';
            rm.setAttribute('aria-label', 'Remove ' + (slot.title || 'item') + ' from bundle');
            rm.dataset.bbRemove = String(i);
            rm.textContent = '×';
            li.appendChild(rm);

            if (slot.sizeCode) {
              const sizeBar = document.createElement('span');
              sizeBar.className = 'bb-slot__size';
              sizeBar.textContent = slot.sizeCode;
              li.appendChild(sizeBar);
            }
          } else {
            li.textContent = '+';
          }
          frag.appendChild(li);
        }
        this.slotsEl.innerHTML = '';
        this.slotsEl.appendChild(frag);
      }

      renderAtc() {
        const count = this.slots.length;
        const hasInvalid = this.slots.some((s) => !s.variantId || s.available === false);
        const disabled = count === 0 || hasInvalid;

        if (this.atcLabel) {
          if (count === 0) {
            this.atcLabel.textContent = 'ADD TO CART';
          } else if (count === 1) {
            this.atcLabel.textContent = 'ADD 1 PAIR TO CART';
          } else {
            this.atcLabel.textContent = 'ADD ' + count + ' PAIRS TO CART';
          }
        }
        this.atcBtn.toggleAttribute('disabled', disabled);
        this.atcBtn.setAttribute('aria-disabled', disabled ? 'true' : 'false');

        if (hasInvalid) {
          this.showError('One or more items in your bundle are sold out at the selected size. Remove them or change size.');
        } else {
          this.clearError();
        }
      }

      /* ---------- Add to Cart ---------- */

      async onAddToCart() {
        if (this.atcBtn.getAttribute('aria-disabled') === 'true') return;
        if (!this.slots.length) return;

        this.clearError();
        this.atcBtn.classList.add('is-loading');

        const items = this.slots.map((s) => ({ id: s.variantId, quantity: 1 }));
        const body = { items };

        // Request updated cart sections inline (same pattern as product-form.js)
        if (this.cart && this.cart.tagName === 'CART-DRAWER') {
          body.sections = ['cart-drawer', 'cart-icon-bubble'];
          body.sections_url = window.location.pathname;
        }

        try {
          const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });

          const json = await res.json();
          if (!res.ok || (json && json.status && json.description)) {
            throw new Error((json && json.description) || 'Failed to add bundle to cart');
          }

          // Klaviyo track (same pattern as product-form.js)
          try {
            if (window._learnq && json.items) {
              json.items.forEach((item) => {
                window._learnq.push([
                  'track',
                  'Added to Cart',
                  {
                    ProductID: String(item.id || item.variant_id || item.product_id),
                    ProductName: item.product_title || item.title,
                    ProductVariant: item.variant_title,
                    Quantity: item.quantity,
                    Price: (item.final_price || item.price || 0) / 100,
                  },
                ]);
              });
            }
          } catch (_) {}

          // Notify rest of theme; cart drawer refresh
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'bundle-builder-au',
              cartData: json,
            });
          }

          const drawer = this.cart || document.querySelector('cart-drawer');
          if (drawer && drawer.tagName === 'CART-DRAWER') {
            if (json.sections && typeof drawer.renderContents === 'function') {
              drawer.renderContents(json);
            } else if (typeof drawer.open === 'function') {
              // Fallback: manually refresh sections and open
              try {
                const [drawerHtml, iconHtml] = await Promise.all([
                  fetch('/?section_id=cart-drawer').then((r) => r.text()),
                  fetch('/?section_id=cart-icon-bubble').then((r) => r.text()),
                ]);
                drawer.renderContents({
                  sections: { 'cart-drawer': drawerHtml, 'cart-icon-bubble': iconHtml },
                });
              } catch (_) {
                drawer.open();
              }
            }
          }

          // Reset the bundle builder back to its default state so the
          // shopper can build another bundle immediately.
          this.resetBundle();
        } catch (err) {
          console.error('[bundle-builder-au] add to cart failed', err);
          this.showError(err.message || 'Something went wrong adding your bundle.');
        } finally {
          this.atcBtn.classList.remove('is-loading');
        }
      }

      /* ---------- Reset bundle to initial state ---------- */

      resetBundle() {
        // Back to 1 Pack, default size, single slot seeded with current product.
        this.packTier = 1;
        this.packButtons.forEach((btn) => {
          const isFirst = btn.dataset.bbPack === '1';
          btn.classList.toggle('is-selected', isFirst);
          btn.setAttribute('aria-checked', isFirst ? 'true' : 'false');
        });

        // Re-resolve default size (first in-stock size for current product)
        const currentVariants =
          (this.data.currentProduct && this.data.currentProduct.variants) || {};
        let defaultSize = '';
        for (const code in currentVariants) {
          const entry = currentVariants[code];
          if (entry && entry.available !== false) {
            defaultSize = code;
            break;
          }
        }
        if (!defaultSize && this.sizeButtons[0]) {
          defaultSize = this.sizeButtons[0].dataset.bbSize || '';
        }
        this.sizeCode = defaultSize;
        this.sizeButtons.forEach((btn) => {
          const isSelected = btn.dataset.bbSize === this.sizeCode;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });

        // Reset slots to just the current product at default size
        this.slots = [];
        const seed = this.buildSlotForProduct(this.data.currentProduct, this.sizeCode);
        if (seed) this.slots.push(seed);

        // Restore PDP visual to the current product (in case style swaps happened)
        if (this.data.currentProduct) {
          this.updatePdpVisual(this.data.currentProduct);
        }

        this.renderPackCards();
        this.renderSlots();
        this.renderStyles();
        this.renderAtc();
      }

      /* ---------- Dynamic checkout (Shop Pay / Afterpay / PayPal) intercept ---------- */

      setupDynamicCheckoutIntercept() {
        const wrap = this.querySelector('.bb-dynamic-checkout');
        if (!wrap) return;
        this._dcBusy = false;

        // If the browser restores the page from bfcache after the shopper
        // navigates back from /checkout, reset the busy flag so the buttons
        // work again.
        window.addEventListener('pageshow', () => {
          this._dcBusy = false;
          clearTimeout(this._dcBusyTimer);
        });

        // The Shop Pay product-page wallet button (<shop-pay-wallet-button>)
        // and the "More payment options" link BOTH skip the cart and open a
        // direct PDP checkout using only the form's current variant. That
        // loses every other bundle slot. So we can't delegate — we have to
        // take over the flow: add the full bundle to the cart ourselves and
        // then navigate to /checkout (where Shop Pay / PayPal / Afterpay are
        // still offered as accelerated methods).
        const handler = async (e) => {
          if (this._dcBusy) return;
          if (!this.slots.length) return;

          // Only intercept clicks inside the Shopify payment-button container.
          const inPaymentBtn = e.target.closest('.shopify-payment-button');
          if (!inPaymentBtn) return;

          e.preventDefault();
          e.stopImmediatePropagation();
          e.stopPropagation();

          this._dcBusy = true;
          // Safety net — if navigation never happens (blocked, cancelled,
          // network error mid-redirect), don't leave the buttons permanently
          // dead. Clear the flag after 8s.
          clearTimeout(this._dcBusyTimer);
          this._dcBusyTimer = setTimeout(() => {
            this._dcBusy = false;
          }, 8000);

          this.clearError();

          try {
            const items = this.slots.map((s) => ({ id: s.variantId, quantity: 1 }));
            const res = await fetch(
              (window.routes && window.routes.cart_add_url) || '/cart/add.js',
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                  'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ items }),
              }
            );
            const json = await res.json().catch(() => ({}));
            if (!res.ok || (json && json.status && json.description)) {
              throw new Error(
                (json && json.description) || 'Failed to add bundle to cart'
              );
            }

            // Navigate to checkout. Shop Pay / Afterpay / PayPal / Google Pay
            // are offered there as accelerated checkout methods, and the full
            // bundle is now in the cart.
            window.location.href = '/checkout';
          } catch (err) {
            console.error('[bundle-builder-au] dynamic checkout add failed', err);
            this._dcBusy = false;
            clearTimeout(this._dcBusyTimer);
            this.showError(
              err.message || 'Could not add your bundle. Please try again.'
            );
          }
        };

        // Use capture phase so we beat any handlers installed inside
        // shopify-accelerated-checkout / shop-pay-wallet-button.
        wrap.addEventListener('click', handler, true);
        // Touch flows can dispatch pointerdown before click and trigger the
        // wallet button early — swallow those too.
        wrap.addEventListener(
          'pointerdown',
          (e) => {
            if (!this.slots.length) return;
            const inPaymentBtn = e.target.closest('.shopify-payment-button');
            if (!inPaymentBtn) return;
            // Don't preventDefault here (it would kill the click entirely);
            // just stop bubbling into the wallet's own pointerdown listener.
            e.stopPropagation();
          },
          true
        );
      }

      /* ---------- Size guide modal ---------- */

      openSizeGuideModal(href) {
        if (!this._sizeGuideModal) {
          const modal = document.createElement('div');
          modal.className = 'bb-modal';
          modal.setAttribute('aria-hidden', 'true');
          modal.innerHTML =
            '<div class="bb-modal__backdrop" data-bb-modal-close></div>' +
            '<div class="bb-modal__dialog" role="dialog" aria-modal="true" aria-label="Size Guide">' +
              '<button type="button" class="bb-modal__close" data-bb-modal-close aria-label="Close size guide">&times;</button>' +
              '<div class="bb-modal__body">' +
                '<div class="bb-modal__loading">Loading size guide…</div>' +
              '</div>' +
            '</div>';
          document.body.appendChild(modal);
          this._sizeGuideModal = modal;

          modal.addEventListener('click', (e) => {
            if (e.target.closest('[data-bb-modal-close]')) this.closeSizeGuideModal();
          });
          this._onEscKey = (e) => {
            if (e.key === 'Escape') this.closeSizeGuideModal();
          };

          // Fetch the size guide page and extract the main content
          fetch(href, { headers: { Accept: 'text/html' } })
            .then((r) => r.text())
            .then((html) => {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const content =
                doc.querySelector('.page__content') ||
                doc.querySelector('main .rte') ||
                doc.querySelector('main .page') ||
                doc.querySelector('#MainContent') ||
                doc.querySelector('main');
              const body = modal.querySelector('.bb-modal__body');
              if (content) {
                body.innerHTML = '';
                body.appendChild(content);
              } else {
                body.innerHTML =
                  '<p style="text-align:center">Unable to load size guide. ' +
                  '<a href="' + href + '" target="_blank" rel="noopener">Open in a new tab</a></p>';
              }
            })
            .catch(() => {
              const body = modal.querySelector('.bb-modal__body');
              if (body) {
                body.innerHTML =
                  '<p style="text-align:center">Unable to load size guide. ' +
                  '<a href="' + href + '" target="_blank" rel="noopener">Open in a new tab</a></p>';
              }
            });
        }

        this._sizeGuideModal.classList.add('is-open');
        this._sizeGuideModal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('bb-modal-open');
        document.addEventListener('keydown', this._onEscKey);
      }

      closeSizeGuideModal() {
        if (!this._sizeGuideModal) return;
        this._sizeGuideModal.classList.remove('is-open');
        this._sizeGuideModal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('bb-modal-open');
        if (this._onEscKey) document.removeEventListener('keydown', this._onEscKey);
      }

      /* ---------- Utilities ---------- */

      showError(msg) {
        if (!this.errorEl) return;
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      formatMoney(cents) {
        const fmt = this.data.moneyFormat || '${{amount}}';
        const dollars = (cents / 100).toFixed(2);
        const withoutDecimals = Math.round(cents / 100).toString();
        return fmt
          .replace(/\{\{\s*amount\s*\}\}/g, dollars)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withoutDecimals)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, dollars.replace('.', ','))
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withoutDecimals);
      }
    }
  );
}
